import { extendObservable } from "mobx";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { delay } from "src/common/delay";

import { MfGridView } from "../components/MfGridView";
import { MfGroupBlockView } from "../components/MfGroupBlockView";
import { MfTabsView } from "../components/MfTabsView";
import { MfVertNavLayout } from "../components/MfVertNavLayout";
import { findAllFields } from "../formNav/findAllFields";
import { findDataNode } from "../formNav/findDataNode";
import { MfPath } from "../MFormTypes";
import {
  ZMfNode,
  ZMfNodeGrid,
  ZMfNodeGroup,
  ZMfNodeTabs,
  ZMfNodeVertNav,
} from "../ZMfNode";
import { createMfNodeBase } from "./createMfNodeBase";
import { MfNode, PropsRender } from "./MfNode";
import { createMfNodeAttr, MfNodeAttr } from "./MfNodeAttr";
import { createMfNodeAttrList } from "./MfNodeAttrList";
import { MfNodeTabs } from "./MfNodeTabs";

let keyBase = 0;
const genKey = (): string => (++keyBase).toString();

const createGrid = (meta: ZMfNodeGrid, dataPath: MfPath): MfNode => {
  const key = genKey();
  const items: MfNode[] = meta.nodes.map((it) => createMfNode(it, dataPath));
  const thisNode: MfNode = extendObservable(createMfNodeBase(meta), {
    meta,
    Render: ({ ctx }: PropsRender) => (
      <MfGridView meta={meta} items={items} ctx={ctx} />
    ),
    key,
    title: meta.title,
    dataPath,
    get value() {
      let dict: Record<string, unknown> = {};
      this.items?.forEach((it) => {
        const { value, dataKey } = it;
        if (value === undefined) return;
        if (dataKey) {
          dict[dataKey] = value;
        } else if (value && typeof value === "object") {
          dict = {
            ...dict,
            ...value,
          };
        }
      });
      return dict;
    },
    setValue(rawValue: unknown) {
      setObjectValue(rawValue, thisNode);
    },
    items,
    colSpan: "*" as const,
    get isChanged(): boolean {
      return this.items.some(({ isChanged }) => isChanged);
    },
    setAsUnchanged() {
      this.items.forEach((it) => it.setAsUnchanged());
    },
  });
  return thisNode;
};

const createMfTabs = (meta: ZMfNodeTabs, dataPath: MfPath): MfNodeTabs => {
  const key = genKey();
  const tabNodes: Record<string, MfNode> = {};
  meta.tabs.forEach((tabDef) => {
    tabNodes[tabDef.key] = createMfNode(tabDef.node, dataPath);
  });
  const thisNode: MfNodeTabs = extendObservable(createMfNodeBase(meta), {
    meta,
    Render: observer(({ ctx }: PropsRender) => (
      <MfTabsView
        meta={meta}
        ctx={ctx}
        activeKey={thisNode.activeKey}
        tabNodes={tabNodes}
        onActiveChange={(newKey) => thisNode.setActiveKey(newKey)}
      />
    )),
    activeKey: meta.defaultTab ?? meta.tabs[0]?.key ?? "",
    setActiveKey(newKey: string) {
      thisNode.activeKey = newKey;
    },
    key,
    dataPath,
    get value() {
      let dict: Record<string, unknown> = {};
      this.items?.forEach((it) => {
        const { value, dataKey } = it;
        if (value === undefined) return;
        if (dataKey) {
          dict[dataKey] = value;
        } else if (value && typeof value === "object") {
          dict = {
            ...dict,
            ...value,
          };
        }
      });
      return dict;
    },
    setValue(rawValue: unknown) {
      setObjectValue(rawValue, thisNode);
    },
    items: Object.values(tabNodes),
    colSpan: "*" as const,
    get isChanged(): boolean {
      return this.items.some(({ isChanged }) => isChanged);
    },
    setAsUnchanged() {
      this.items.forEach((it) => it.setAsUnchanged());
    },
    async activate(_fieldDataKey?: string, path?: MfPath) {
      const needKey = path?.[1];
      if (needKey) {
        // Поиск вкладки, в которой находится активируемое поле
        const pair = Object.entries(tabNodes).find(([, node]) => {
          const subNodes: MfNodeAttr[] = [];
          findAllFields(node, subNodes);
          return subNodes.some(({ dataKey }) => dataKey === needKey);
        });
        // Переключение на нужную вкладку
        if (pair && pair[0] !== this.activeKey) {
          this.setActiveKey(pair[0]);
          await delay(10);
        }
      }
    },
  });
  return thisNode;
};

const setObjectValue = (rawValue: unknown, node: MfNode) => {
  if (rawValue && typeof rawValue === "object") {
    Object.entries(rawValue).forEach(([key, value]) => {
      const subNode = findDataNode(node, [key]);
      subNode?.setValue(value);
    });
  }
};

const createGroup = (meta: ZMfNodeGroup, ownerDataPath: MfPath): MfNode => {
  const groupName = meta.name;
  const dataPath = [...ownerDataPath, groupName];
  const contentNode = createMfNode(meta.content.node, dataPath);
  return extendObservable(createMfNodeBase(meta), {
    meta,
    dataPath,
    Render: ({ ctx }: PropsRender) => (
      <MfGroupBlockView meta={meta} content={contentNode} ctx={ctx} />
    ),
    get value() {
      return contentNode.value;
    },
    setValue(newValue: undefined) {
      contentNode.setValue(newValue);
    },
    get key() {
      return meta.name;
    },
    get dataKey() {
      return meta.name;
    },
    get items() {
      return [contentNode];
    },
    get title() {
      return meta.title;
    },
    colSpan: "*" as const,
    get isChanged(): boolean {
      return this.items.some(({ isChanged }) => isChanged);
    },
    setAsUnchanged() {
      this.items.forEach((it) => it.setAsUnchanged());
    },
  });
};

const createVertNav = (meta: ZMfNodeVertNav, dataPath: MfPath): MfNode => {
  const items = meta.nodes.map((m) => createMfNode(m, dataPath));
  const thisNode: MfNode = extendObservable(createMfNodeBase(meta), {
    meta,
    key: genKey(),
    dataPath,
    items,
    get value() {
      const dict: Record<string, unknown> = {};
      this.items?.forEach(({ dataKey, value }) => {
        if (dataKey && value !== undefined) {
          dict[dataKey] = value;
        }
      });
      return dict;
    },
    setValue(newValue: unknown) {
      setObjectValue(newValue, thisNode);
    },
    Render: ({ ctx }: PropsRender) => (
      <MfVertNavLayout meta={meta} items={items} ctx={ctx} />
    ),
    colSpan: "*" as const,
    get isChanged(): boolean {
      return this.items.some(({ isChanged }) => isChanged);
    },
    setAsUnchanged() {
      this.items.forEach((it) => it.setAsUnchanged());
    },
  });
  return thisNode;
};

// Нам нужно, чтобы компилятор контролировал обработку всех типов. Поэтому нам не нужен consistent-return и default-case
// eslint-disable-next-line consistent-return
export const createMfNode = (meta: ZMfNode, ownerDataPath: MfPath): MfNode => {
  // eslint-disable-next-line default-case
  switch (meta.type) {
    case "attr":
      if (meta.attr.type.name === "list") {
        return createMfNodeAttrList(meta, ownerDataPath, meta.attr.type);
      }
      return createMfNodeAttr(meta, ownerDataPath);
    case "grid":
      return createGrid(meta, ownerDataPath);
    case "group":
      return createGroup(meta, ownerDataPath);
    case "tabs":
      return createMfTabs(meta, ownerDataPath);
    case "vertNav":
      return createVertNav(meta, ownerDataPath);
  }
};

export const createMfRootNode = (meta: ZMfNode): MfNode =>
  createMfNode(meta, []);
